import { apiClient } from './api';

export interface SyncResult {
  success: boolean;
  sync_timestamp: string;
  total_fetched?: number;
  processed_calls?: number;
  new_calls?: number;
  updated_calls?: number;
  errors?: number;
  sync_type?: string;
  error?: string;
}

export interface SyncStatus {
  customer_id: string;
  configuration: {
    data_source_mode: 'webhook' | 'api';
    webhook_enabled: boolean;
    api_sync_enabled: boolean;
  };
  scheduler_status: {
    is_running: boolean;
    sync_interval_minutes: number;
    last_sync_time: string | null;
    last_sync_result: SyncResult | null;
    api_configured: boolean;
    next_sync_in_seconds: number | null;
  };
  data_source_active: string;
  can_refresh: boolean;
}

export interface RefreshResponse {
  message: string;
  customer_id: string;
  sync_result: SyncResult;
}

export interface ConfigureResponse {
  message: string;
  customer_id: string;
  configuration: {
    data_source_mode: string;
    webhook_enabled: boolean;
    api_sync_enabled: boolean;
  };
}

export interface HealthResponse {
  sync_service: string;
  configuration: {
    data_source_mode: string;
    webhook_enabled: boolean;
    api_sync_enabled: boolean;
  };
  scheduler_running: boolean;
  api_configured: boolean;
  last_sync: string | null;
  status: string;
  message: string;
}

export interface TestApiResponse {
  message: string;
  customer_id: string;
  test_result: {
    api_configured: boolean;
    calls_fetched: number;
    sample_call_ids: string[];
  };
}

export const dataSyncApi = {
  /**
   * Trigger manual refresh of call data
   */
  async refreshCalls(forceFullSync: boolean = false): Promise<RefreshResponse> {
    return await apiClient.post<RefreshResponse>(`/api/sync/refresh?force_full_sync=${forceFullSync}`);
  },

  /**
   * Get current sync status and configuration
   */
  async getSyncStatus(): Promise<SyncStatus> {
    return await apiClient.get<SyncStatus>('/api/sync/status');
  },

  /**
   * Configure data source mode (webhook vs API)
   */
  async configureDataSource(mode: 'webhook' | 'api'): Promise<ConfigureResponse> {
    return await apiClient.post<ConfigureResponse>(`/api/sync/configure?mode=${mode}`);
  },

  /**
   * Get sync service health status
   */
  async getHealthStatus(): Promise<HealthResponse> {
    return await apiClient.get<HealthResponse>('/api/sync/health');
  },

  /**
   * Test Voice Assistant API connection
   */
  async testApiConnection(): Promise<TestApiResponse> {
    return await apiClient.post<TestApiResponse>('/api/sync/test-api');
  }
};